// Mirror of backend/app/utils/experience_range.py — keep output identical.

export interface ExperienceRangeFields {
  min_experience_years?: number | null;
  min_experience_months?: number | null;
  max_experience_years?: number | null;
  max_experience_months?: number | null;
}

const clampInt = (value: unknown): number => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
};

const joinYM = (years: number, months: number, verbose: boolean): string => {
  const parts: string[] = [];
  if (years > 0) {
    parts.push(verbose ? `${years} year${years === 1 ? '' : 's'}` : `${years}y`);
  }
  if (months > 0) {
    parts.push(verbose ? `${months} month${months === 1 ? '' : 's'}` : `${months}m`);
  }
  if (parts.length === 0) return verbose ? '0 months' : '0';
  return parts.join(' ');
};

export function formatExperienceRange(
  minYears: number | null | undefined,
  minMonths: number | null | undefined,
  maxYears: number | null | undefined,
  maxMonths: number | null | undefined,
  options: { verbose?: boolean } = {}
): string {
  const verbose = !!options.verbose;
  const minY = clampInt(minYears);
  const minM = clampInt(minMonths);
  const maxY = clampInt(maxYears);
  const maxM = clampInt(maxMonths);
  const totalMin = minY * 12 + minM;
  const totalMax = maxY * 12 + maxM;

  if (totalMin === 0 && totalMax === 0) return 'Any experience';
  if (totalMax === 0) {
    return joinYM(minY, minM, verbose) + (verbose ? ' or more' : '+');
  }
  if (totalMin === 0) {
    return 'Up to ' + joinYM(maxY, maxM, verbose);
  }
  if (totalMin === totalMax) {
    return joinYM(minY, minM, verbose) + (verbose ? ' exactly' : '');
  }
  const separator = verbose ? ' to ' : ' – ';
  return joinYM(minY, minM, verbose) + separator + joinYM(maxY, maxM, verbose);
}

export function formatJobExperience(
  job: ExperienceRangeFields | null | undefined,
  options: { verbose?: boolean } = {}
): string {
  if (!job) return '';
  return formatExperienceRange(
    job.min_experience_years,
    job.min_experience_months,
    job.max_experience_years,
    job.max_experience_months,
    options
  );
}

export function hasExperienceRange(job: ExperienceRangeFields | null | undefined): boolean {
  if (!job) return false;
  const minTotal = clampInt(job.min_experience_years) * 12 + clampInt(job.min_experience_months);
  const maxTotal = clampInt(job.max_experience_years) * 12 + clampInt(job.max_experience_months);
  return minTotal > 0 || maxTotal > 0;
}
